import { Link } from 'react-router-dom';

interface ComicCardProps {
  id: number;
  title: string;
  author: string;
  status: string;
  rating: number;
  coverImage: string;
  category: string;
  tags?: string[];
  chapters?: number;
  slug: string;
  variant?: 'default' | 'compact';
}

export default function ComicCard({
  id,
  title,
  author,
  status,
  rating,
  coverImage,
  category,
  tags,
  chapters,
  slug,
  variant = 'default'
}: ComicCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to="/category/all"
        className="group flex gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors duration-200"
        data-product-shop
      >
        <div className="w-16 h-[88px] shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={coverImage}
            alt={`${title} 韩国漫画封面`}
            title={`${title} - 好看的韩国漫画`}
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
        </div>
        <div className="flex flex-col justify-between min-w-0 py-1">
          <div>
            <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
              {title}
            </h4>
            <p className="text-xs text-foreground-500 mt-0.5">{author}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs px-1.5 py-0.5 rounded bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
              {category}
            </span>
            <span className="text-xs text-foreground-400">{chapters}话</span>
            <span className="flex items-center gap-0.5 text-xs text-accent-600">
              <i className="ri-star-fill text-[10px]"></i>
              {rating}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to="/category/all"
      className="group block rounded-lg overflow-hidden border border-background-200/70 bg-background-50 hover:border-primary-200 transition-all duration-300"
      data-product-shop
    >
      {/* Cover Image */}
      <div className="relative aspect-[5/7] overflow-hidden bg-background-100">
        <img
          src={coverImage}
          alt={`${title} 韩国漫画封面`}
          title={`${title} - 好看的韩国漫画`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        {/* Status Badge */}
        <div className="absolute top-2 left-2">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
            status === '连载中' ? 'bg-primary-500 text-background-50' : 'bg-background-800 text-background-50'
          }`}>
            {status}
          </span>
        </div>
        {/* Rating Badge */}
        <div className="absolute top-2 right-2 flex items-center gap-0.5 bg-background-950/70 backdrop-blur-sm text-accent-400 text-xs px-1.5 py-0.5 rounded-full">
          <i className="ri-star-fill text-[10px]"></i>
          {rating}
        </div>
      </div>
      {/* Info */}
      <div className="p-3 md:p-4">
        <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
          {title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1">{author}</p>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
            {category}
          </span>
          {tags && tags.slice(0, 2).map(tag => (
            <span key={tag} className="text-xs text-foreground-400 whitespace-nowrap">
              #{tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}